import { useEffect, useMemo, useState } from 'react';
import { adminCreatePost, adminDeletePost, adminFetchPosts, adminUpdatePost } from '../../lib/db';
import { notifyAdminContentUpdated } from '../../lib/site-settings';
import type { Post, PostCategory } from '../../lib/types';
import { Loader2, Plus, Save, Trash2, PencilLine, Eye, CheckCircle2, AlertCircle } from 'lucide-react';

const emptyForm = {
  title: '',
  slug: '',
  excerpt: '',
  content: '',
  category: 'news' as PostCategory,
  cover_image_url: '',
  published: true,
};

export default function AdminPosts() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    void loadPosts();
  }, []);

  async function loadPosts() {
    setLoading(true);
    try {
      const data = await adminFetchPosts();
      setPosts(data);
    } catch {
      setStatus('error');
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setEditingId(null);
    setForm(emptyForm);
  }

  function startEdit(post: Post) {
    setEditingId(post.id);
    setForm({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      category: post.category,
      cover_image_url: post.cover_image_url,
      published: post.published,
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setStatus('idle');
    try {
      if (editingId) {
        await adminUpdatePost(editingId, {
          ...form,
          slug: form.slug || form.title,
        });
      } else {
        await adminCreatePost({
          ...form,
          slug: form.slug || form.title,
        });
      }
      await loadPosts();
      notifyAdminContentUpdated();
      resetForm();
      setStatus('success');
      window.setTimeout(() => setStatus('idle'), 2500);
    } catch {
      setStatus('error');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Delete this post?')) return;
    try {
      await adminDeletePost(id);
      await loadPosts();
      notifyAdminContentUpdated();
      if (editingId === id) resetForm();
      setStatus('success');
    } catch {
      setStatus('error');
    }
  }

  const publishedCount = useMemo(() => posts.filter((post) => post.published).length, [posts]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold text-stone-900">Posts & Stories</h2>
          <p className="text-stone-500 mt-1">Create, edit, and publish content for the website.</p>
        </div>
        <button onClick={resetForm} className="btn-primary inline-flex items-center gap-2 self-start">
          <Plus className="h-4 w-4" />
          New Post
        </button>
      </div>

      {status === 'success' && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700 flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4" />
          Your content was saved successfully.
        </div>
      )}
      {status === 'error' && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          Something went wrong. Please try again.
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <form onSubmit={handleSubmit} className="card p-5 sm:p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-display text-lg font-semibold text-stone-900">
                {editingId ? 'Edit Post' : 'Create Post'}
              </h3>
              <p className="text-sm text-stone-500">Everything here updates the public site.</p>
            </div>
            <div className="rounded-full bg-brand-50 px-3 py-1 text-sm font-medium text-brand-700">
              {publishedCount} live
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="label">Title</label>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input" placeholder="Post title" required />
            </div>
            <div>
              <label className="label">Slug</label>
              <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="input" placeholder="post-slug" />
            </div>
            <div>
              <label className="label">Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value as PostCategory })} className="input">
                <option value="news">News</option>
                <option value="programs">Programs</option>
                <option value="impact">Impact</option>
                <option value="events">Events</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="label">Cover Image URL</label>
              <input value={form.cover_image_url} onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })} className="input" placeholder="https://..." />
            </div>
            <div className="md:col-span-2">
              <label className="label">Excerpt</label>
              <textarea value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="input min-h-[90px]" placeholder="Short summary" />
            </div>
            <div className="md:col-span-2">
              <label className="label">Content</label>
              <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="input min-h-[180px]" placeholder="Full article content" />
            </div>
            <div className="flex items-center gap-3 rounded-lg border border-stone-200 bg-stone-50 p-3 md:col-span-2">
              <input id="published" type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="h-4 w-4 rounded border-stone-300 text-brand-600" />
              <label htmlFor="published" className="text-sm text-stone-700">Publish this post immediately</label>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button type="submit" disabled={saving} className="btn-primary inline-flex items-center gap-2">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {editingId ? 'Update Post' : 'Save Post'}
            </button>
            {editingId && (
              <button type="button" onClick={resetForm} className="btn-outline">
                Cancel
              </button>
            )}
          </div>
        </form>

        <div className="space-y-4">
          <div className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
            <h3 className="font-display text-lg font-semibold text-stone-900">Existing Posts</h3>
            <p className="mt-1 text-sm text-stone-500">Tap any card to edit it.</p>
          </div>

          {loading ? (
            <div className="flex justify-center rounded-2xl border border-stone-200 bg-white p-8">
              <Loader2 className="h-6 w-6 animate-spin text-brand-500" />
            </div>
          ) : posts.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-stone-300 bg-stone-50 p-8 text-center text-sm text-stone-500">
              No posts yet. Create your first story.
            </div>
          ) : (
            <div className="space-y-3">
              {posts.map((post) => (
                <div key={post.id} className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <button onClick={() => startEdit(post)} className="flex-1 text-left">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium uppercase tracking-wide text-brand-700">{post.category}</span>
                        {!post.published && <span className="rounded-full bg-stone-100 px-2.5 py-1 text-xs font-medium text-stone-600">Draft</span>}
                      </div>
                      <h4 className="mt-2 font-display text-base font-semibold text-stone-900">{post.title}</h4>
                      <p className="mt-1 text-sm text-stone-500 line-clamp-2">{post.excerpt || 'No excerpt yet.'}</p>
                    </button>
                    <div className="flex gap-2">
                      <button onClick={() => startEdit(post)} className="rounded-lg border border-stone-200 p-2 text-stone-600 hover:text-brand-700" title="Edit">
                        <PencilLine className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleDelete(post.id)} className="rounded-lg border border-red-200 p-2 text-red-600 hover:bg-red-50" title="Delete">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  {post.published && (
                    <a href={`/programs/${post.slug}`} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-brand-700">
                      <Eye className="h-4 w-4" /> Preview live page
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
